"use client";

import { useEffect, useState, useMemo } from "react";
import StorePage from "./store-page";
import AuthPage from "../auth/auth-page";
import ProductPage from "../product/product-page";
import ProfileContainer from "../profile/profile-container";
import StoreModal from "./store-modal";
import MarketPlace from "../market-place/marketplace";
import ModalContainer from "../modal/modal-container";
import NotFound from "../common/NotFound";
import MarketplaceCartPage from "./marketplaceCartPage";
import CartPage from "./cart-page";
import {
  generateProductUrl,
  getLoggedinUser,
  useActiveCart,
} from "../../lib/util";
import { getActiveUser } from "../slice/auth-slice";
import { useAppDispatch, useAppSelector } from "../slice/hooks";
import { getCartByOrgAndAccessPoint, getCartSource, setCartSource, updateCart } from "../slice/cart-slice";
import { useHistoryManager } from "../history/useHistoryManager";
import { useModalManager } from "../modal/useModalManager";
import {
  getStorePath,
  getUrlData,
  getUrlPath,
  updateFullPath,
} from "../slice/path-slice";
import {
  getAccessPoint,
  getAccessPointId,
  getAccessPoints,
  getOrganisation,
  setAccessPoint,
  setAccessPointId,
  setAccessPoints,
  setOrganisation,
} from "../slice/accessOrg-slice";
import {
  getCategories,
  setActiveCategory,
  setCategories,
} from "../slice/category-slice";
import { store } from "../slice/store";
import MarketPlaceStore from "../market-place/marketplaceStore";

const Container = ({
  data,
  fullUrlPath,
  urlPath: initialPath,
  basePath,
  location,
  navLocation = null,
  store = "shop",
}) => {
  const dispatch = useAppDispatch();
  const { tokenData } = useAppSelector(getActiveUser);
  const { loggedInUser } = useAppSelector((state) =>
    getActiveUser(state, getLoggedinUser()),
  );

  const initializedPath = useMemo(() => {
    if (initialPath === "market-place") {
      return {
        storePath: basePath,
        basePath,
      };
    }

    const normalizedStorePath = location
      ? `${basePath.replace(/\/$/, "")}/${location.replace(/^\//, "")}`
      : basePath;

    return {
      storePath: normalizedStorePath,
      basePath,
    };
  }, [location, initialPath, basePath]);

  const storePath = useAppSelector((state) =>
    getStorePath(state, initializedPath.storePath),
  );

  useEffect(() => {
    dispatch(updateFullPath(initializedPath));
  }, []);

  const urlPath = useAppSelector((state) => getUrlPath(state, initialPath));
  const urlData = useAppSelector((state) => getUrlData(state, data?.urlData));

  const { exitPathOnLoad, navigateTo } = useHistoryManager(storePath, urlPath);

  const [searchTerm, setSearchTerm] = useState("");

  const accessPointId = useAppSelector((state) =>
    getAccessPointId(state, data?.accessPointId),
  );

  const organisation = useAppSelector((state) =>
    getOrganisation(state, data?.org?.organisation),
  );

  const accessPoint = useAppSelector((state) =>
    getAccessPoint(
      state,
      data?.accessPoints?.find((ap) => ap.accessPointId === data.accessPointId),
    ),
  );

  const accessPoints = useAppSelector((state) =>
    getAccessPoints(state, data?.accessPoints),
  );

  const cartSource = useAppSelector((state) =>
    getCartSource(state, store === "shop" ? "SHOP" : "MARKETPLACE"),
  );

  const serverToken = data?.token || null;

  const categories = useAppSelector((state) =>
    getCategories(state, data?.categories),
  );

  const [showProgress, setShowProgress] = useState(false);
  const [checkoutState, setCheckoutState] = useState("cartItems");

  const cart = useAppSelector((state) =>
    getCartByOrgAndAccessPoint(
      state,
      organisation?.orgId,
      accessPoint?.accessPointId,
      data.cart,
    ),
  );

  const {
    modal,
    modalData,
    modalState,
    modalBackButton,
    openModal,
    closeModal,
    setModalBackButton,
  } = useModalManager(storePath, showProgress, setShowProgress);

  const { isLoading: isCartLoading, mutate: mutateActiveCart } = useActiveCart({
    onSuccess: (response) => {
      const cart = response.data.collection;
      dispatch(updateCart(cart.profileId, cart.accessPointId, cart));
    },
    onError: (error) => {
      console.error("Failed to fetch active cart:", error);
    },
  });

  useEffect(() => {
    dispatch(setCartSource(cartSource));
  }, [cartSource]);

  // Initial navigation guard
  useEffect(() => {
    exitPathOnLoad("auth", loggedInUser);
  }, [loggedInUser]);

  useEffect(() => {
    if (!accessPoint || accessPoint.accessPointId !== accessPointId) {
      const foundAp =
        accessPoints?.find((ap) => ap.accessPointId === accessPointId) ||
        accessPoint;
      if (foundAp) {
        dispatch(setAccessPoint(foundAp));
      }
    }
  }, [accessPointId, accessPoints, accessPoint]);

  // Sync cart from backend if token is present and not in Marketplace
  useEffect(() => {
    // Prevent fetching cart in Marketplace
    if (storePath?.toLowerCase().includes("/market-place")) return;
    if (tokenData && accessPointId) {
      mutateActiveCart({
        orgId: organisation?.orgId,
        accessPointId,
        token: tokenData.token,
        cartSource,
      });
    }
  }, [tokenData, accessPointId, storePath]);

  // Sync cart from props on mount
  useEffect(() => {
    if (data.cart) {
      const cart = data.cart;
      dispatch(updateCart(cart.profileId, cart.accessPointId, cart));
    }
  }, []);

  // Helpers
  const showCart = () => {
    const checkoutPath =
      storePath === "/" || storePath === "market-place"
        ? "checkout"
        : `${storePath.replace(/\/+$/, "")}/checkout`;

    if (navLocation === "marketplace") {
      (console.log({ accessPoint }),
        navigateTo(
          "market-place/checkout",
          {
            urlData: {
              accessPoint,
              accessPoints,
              accessPointId,
              organisation,
            },
            onNavigate: (navigate) => {
              if (navigate) {
                // dispatch(
                //   updateFullPath({
                //     storePath: "market-place",
                //     basePath: "market-place",
                //   })
                // );
                dispatch(setAccessPointId(null));
                dispatch(setAccessPoint(null));
                dispatch(setAccessPoints([]));
                dispatch(setOrganisation(null));
                dispatch(setActiveCategory(null));
                dispatch(setCategories(null));
              }
            },
          },
          false,
        ));
    } else {
      openModal("checkout", {}, checkoutPath);
    }
  };

  const showProduct = (product, incomingStorePath = "") => {
    const path =
      incomingStorePath && navLocation === "marketplace"
        ? incomingStorePath
        : storePath;

    const productUrlPath = generateProductUrl(path, product);

    if (navLocation === "marketplace" && !modal) {
      navigateTo(
        productUrlPath,
        {
          urlData: {
            product,
            accessPoint,
            accessPoints,
            accessPointId,
            organisation,
            storePath,
          },
        },
        false,
      );

      window.scrollTo(0, 0);
    } else {
      openModal("product", product, productUrlPath);
    }
  };

  const activateBackButton = (show, onClick = null) => {
    setModalBackButton({ show, onClick });
  };

  const commonProps = useMemo(
    () => ({
      data,
      showCart,
      cart,
      accessPointId,
      storePath,
      searchTerm,
      setSearchTerm,
      modal,
      activateBackButton,
      urlData,
      urlPath,
      fullUrlPath,
      organisation,
      accessPoint,
      categories,
      serverToken,
      accessPoints,
      navLocation,
    }),
    [
      data,
      cart,
      accessPointId,
      storePath,
      searchTerm,
      urlData,
      urlPath,
      fullUrlPath,
      organisation,
      accessPoint,
      categories,
      serverToken,
      accessPoints,
      navLocation,
    ],
  );

  // View router
  const renderView = () => {
    switch (urlPath) {
      case "home":
        return <StorePage {...commonProps} showProduct={showProduct} />;
      case "product":
        return <ProductPage {...commonProps} />;
      case "auth":
        return <AuthPage {...commonProps} />;
      case "checkout":
        if (storePath === "market-place") {
          return (
            <MarketplaceCartPage
              {...commonProps}
              showProgress={showProgress}
              showProduct={showProduct}
              setShowProgress={setShowProgress}
              checkoutState={checkoutState}
              setCheckoutState={setCheckoutState}
              closeModal={closeModal}
            />
          );
        }
        if (storePath !== "market-place") {
          return (
            <CartPage
              {...commonProps}
              showProgress={showProgress}
              showProduct={showProduct}
              setShowProgress={setShowProgress}
              checkoutState={checkoutState}
              setCheckoutState={setCheckoutState}
            />
          );
        }
      case "profile":
        return <ProfileContainer {...commonProps} />;
      case "market-place":
        return <MarketPlace {...commonProps} />;
      case "stores":
        return <MarketPlaceStore {...commonProps} />;
      default:
        return <NotFound />;
    }
  };

  return (
    <>
      {renderView()}
      {modal && (
        <ModalContainer
          closeModal={closeModal}
          modalBackButton={modalBackButton}
        >
          <StoreModal
            closeModal={closeModal}
            showCart={showCart}
            showProduct={showProduct}
            activateBackButton={activateBackButton}
            cart={cart}
            storePath={storePath}
            modalData={modalData}
            modalState={modalState}
            accessPoint={accessPoint}
            showProgress={showProgress}
            setShowProgress={setShowProgress}
            checkoutState={checkoutState}
            setCheckoutState={setCheckoutState}
            organisation={organisation}
          />
        </ModalContainer>
      )}
    </>
  );
};

export default Container;
